// Receiving a room from the World Editor and playing it.
//
// The two halves are separate Vite apps on separate origins, so they share no
// storage and the handoff has to be explicit. It is a postMessage handshake
// rather than a URL payload: the editor opens the game, the game says it is
// listening, the editor sends the room. That has no size ceiling (room9 is ~25 KB
// and the payload carries physics and a rune list beside the room) and needs no
// encoding library.
//
// What a playtest deliberately is *not*: the game. The room is injected, so it is
// not reached through the real door graph with the real locks and story gating.
// This verifies that a puzzle works, not that it sits right in the sequence.
import { IS_PLAYTEST } from './playtestStorage';
import { PLAYTEST_HASH, PLAYTEST_PAYLOAD_MESSAGE, PLAYTEST_READY_MESSAGE, PlaytestPayload, decodePlaytest, loadPlaytest } from '../../rune-lab/src/sim/playtest';
import { RoomDef } from '../../rune-lab/src/sim/types';
import { buildAllRooms } from '../../rune-lab/src/sim/rooms';
import { getWorld } from './activeWorld';
import { learnRune, resetLearnedRunes } from './learnedRunes';

/** Long enough to cover a cold Vite dev-server start, short enough that a game
 * opened with the flag by hand does not hang on a message that is never coming. */
const HANDSHAKE_TIMEOUT_MS = 20_000;

let payload: PlaytestPayload | null = null;

export function isPlaytest(): boolean {
  return IS_PLAYTEST;
}

/** The room being playtested, or null in ordinary play. */
export function playtestPayload(): PlaytestPayload | null {
  return payload;
}

/** Every room the game should have on hand: the shipped world, with the
 * playtested room laid over it.
 *
 * Replaces the three `buildAllRooms()` calls in GameScene rather than adding a
 * fourth path, so boot, New Game and Load Game all agree about which world they
 * are in. Overlaying rather than replacing wholesale keeps the shipped rooms
 * loadable, which matters because a playtested room can be an *edit* of one the
 * others' doors still point at. */
export function buildRoomsForPlay(): Record<string, RoomDef> {
  const rooms = buildAllRooms();
  if (payload) rooms[payload.room.id] = structuredClone(payload.room);
  return rooms;
}

/** Waits for the editor to send a room, then applies everything about it that is
 * not the room itself. Resolves to false if nothing arrives, in which case the
 * game just boots normally.
 *
 * Called before the Phaser game is constructed, so the scene's first frame is
 * already the playtested world -- rather than booting into room0 and jumping,
 * which would run every arrival effect for a room nobody asked to see. */
export async function awaitPlaytest(): Promise<boolean> {
  if (!IS_PLAYTEST) return false;

  // A room carried in the link itself, which is the path that works on a phone:
  // no second window, no opener, no handshake. Tried first because when it is
  // there it is unambiguous -- somebody put a room in this URL on purpose.
  const inLink = new URLSearchParams(location.hash.replace(/^#/, '')).get(PLAYTEST_HASH);
  if (inLink) {
    try {
      return apply(await decodePlaytest(inLink));
    } catch (err) {
      console.error('[playtest] refused the link:', err);
      return false;
    }
  }

  // No opener means nobody is going to send anything -- someone opened the game
  // with the flag by hand, or reloaded a playtest tab after closing the editor.
  // Waiting out the timeout for a message that cannot arrive would just be a
  // twenty-second black screen.
  if (!window.opener) return false;
  const received = await new Promise<PlaytestPayload | null>((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, HANDSHAKE_TIMEOUT_MS);
    function onMessage(event: MessageEvent): void {
      // Only from the window that opened us. Not a security boundary -- this is a
      // local dev tool -- but it keeps an unrelated frame's chatter from being
      // parsed as a room.
      if (event.source !== window.opener) return;
      if ((event.data as { type?: string } | null)?.type !== PLAYTEST_PAYLOAD_MESSAGE) return;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      try {
        resolve(loadPlaytest((event.data as { payload: unknown }).payload));
      } catch (err) {
        console.error('[playtest] refused the payload:', err);
        resolve(null);
      }
    }
    window.addEventListener('message', onMessage);
    // Say we are listening only once a listener actually is: the editor sends on
    // this, so a ready sent earlier would race the room against its own handler.
    window.opener?.postMessage({ type: PLAYTEST_READY_MESSAGE }, '*');
  });

  if (!received) return false;
  return apply(received);
}

/** Everything about a payload that is not the room itself. Shared by both
 * deliveries, so a link and a handshake cannot end up meaning different things. */
function apply(received: PlaytestPayload): boolean {
  payload = received;
  const world = getWorld();
  world.physics = received.physics;
  // Authoritative, not additive -- "what if they got here without Reverse" is as
  // much a playtest as "with everything".
  resetLearnedRunes();
  for (const id of received.runes) learnRune(id);
  return true;
}
